import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';

interface DetailItem {
  label: string;
  href: string;
}

interface SubItem {
  label: string;
  href: string;
  details?: DetailItem[];
}

interface NavItem {
  label: string;
  href: string;
  subs?: SubItem[];
}

const navItems: NavItem[] = [
  {
    label: 'HOME',
    href: '/',
  },
  {
    label: 'BRAND',
    href: '/brand/about',
    subs: [
      {
        label: '브랜드 소개',
        href: '/brand/about',
      },
      {
        label: '위생 및 안전 기준',
        href: '/brand/safety',
      },
      {
        label: '아티스트 크루',
        href: '/brand/artists',
        details: [
          { label: '시니어 아티스트', href: '/brand/artists/senior' },
          { label: '주니어 아티스트', href: '/brand/artists/junior' },
        ],
      },
    ],
  },
  {
    label: 'GALLERY',
    href: '/portfolio',
    subs: [
      {
        label: '장르별 포트폴리오',
        href: '/portfolio/genre',
        details: [
          { label: '블랙 앤 그레이', href: '/portfolio/genre/black-gray' },
          { label: '미니멀 & 라인워크', href: '/portfolio/genre/minimal' },
          { label: '이레즈미 & 올드스쿨', href: '/portfolio/genre/irezumi' },
        ],
      },
      {
        label: '부위별 포트폴리오',
        href: '/portfolio/body-part',
      },
    ],
  },
  {
    label: 'FRANCHISE',
    href: '/franchise/guide',
    subs: [
      {
        label: '창업 안내',
        href: '/franchise/guide',
        details: [
          { label: '개설 절차 & 창업 비용', href: '/franchise/guide' },
        ],
      },
      {
        label: '본사 지원 시스템',
        href: '/franchise/support',
        details: [
          { label: '마케팅 지원', href: '/franchise/support/marketing' },
          { label: '물류 및 소모품', href: '/franchise/support/logistics' },
        ],
      },
      {
        label: '인테리어 컨셉',
        href: '/franchise/interior',
      },
      {
        label: '가맹점 예상 수익',
        href: '/franchise/revenue',
      },
      {
        label: '가맹 성공 사례',
        href: '/franchise/cases',
      },
      {
        label: '가맹점 찾기',
        href: '/franchise/stores',
      },
    ],
  },
  {
    label: 'COMMUNITY',
    href: '/community/notice',
    subs: [
      { label: '공지사항', href: '/community/notice' },
      { label: '이벤트', href: '/community/events' },
      { label: '고객 문의', href: '/community/inquiry' },
    ],
  },
];

const menuItemsWithSubs = navItems.filter((item) => item.subs && item.subs.length > 0);

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const [hoveredCategory, setHoveredCategory] = useState<string>(
    menuItemsWithSubs[0]?.label ?? ''
  );
  const [contentVisible, setContentVisible] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState<string | null>(null);
  const location = useLocation();
  const leaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const contentTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMegaOpen(false);
    setMobileOpen(false);
  }, [location.pathname]);

  const handleNavMouseEnter = () => {
    if (leaveTimerRef.current) clearTimeout(leaveTimerRef.current);
    setMegaOpen(true);
  };

  const handleNavMouseLeave = () => {
    leaveTimerRef.current = setTimeout(() => {
      setMegaOpen(false);
    }, 120);
  };

  const handleCategoryChange = (label: string) => {
    if (label === hoveredCategory) return;
    setContentVisible(false);
    if (contentTimerRef.current) clearTimeout(contentTimerRef.current);
    contentTimerRef.current = setTimeout(() => {
      setHoveredCategory(label);
      setContentVisible(true);
    }, 120);
  };

  const isScrolledOrMenuOpen = scrolled || megaOpen;

  const activeNavItem = navItems.find((item) => item.label === hoveredCategory);

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolledOrMenuOpen
            ? 'bg-white/80 backdrop-blur-xl border-b border-white/40'
            : 'bg-transparent'
        }`}
      >
        <div className="px-8 md:px-16 flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-1.5 cursor-pointer flex-shrink-0 group">
            <img
              src="https://public.readdy.ai/ai/img_res/8a4691bb-c8bc-42bc-8a41-719182ce50df.png"
              alt="선율 로고"
              className="w-10 h-10 md:w-14 md:h-14 object-contain flex-shrink-0 transition-all duration-300"
              style={{ filter: isScrolledOrMenuOpen ? 'none' : 'invert(1)' }}
            />
            <div className="flex flex-col items-start">
              <span
                className={`text-xl md:text-2xl font-light tracking-[0.22em] leading-none transition-colors duration-300 ${
                  isScrolledOrMenuOpen ? 'text-[#222222]' : 'text-white'
                }`}
                style={{ fontFamily: "'Cinzel', 'Nanum Myeongjo', serif" }}
              >
                선율
              </span>
              <span
                className={`text-[8px] tracking-[0.38em] uppercase font-light leading-none mt-0.5 transition-colors duration-300 ${
                  isScrolledOrMenuOpen ? 'text-[#AAAAAA]' : 'text-white/55'
                }`}
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              >
                SEONYUL LINE STUDIO
              </span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav
            className="hidden md:flex items-center gap-10"
            onMouseEnter={handleNavMouseEnter}
            onMouseLeave={handleNavMouseLeave}
          >
            {navItems.map((item) => (
              <div
                key={item.label}
                className="relative"
                onMouseEnter={() => {
                  if (item.subs) handleCategoryChange(item.label);
                }}
              >
                <Link
                  to={item.href}
                  className={`text-[11px] tracking-[0.22em] uppercase font-medium transition-colors duration-300 cursor-pointer whitespace-nowrap py-6 inline-block ${
                    isScrolledOrMenuOpen
                      ? 'text-[#222222] hover:text-[#888]'
                      : 'text-white hover:text-white/70'
                  }`}
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                >
                  {item.label}
                </Link>
              </div>
            ))}
            <Link
              to="/community/inquiry"
              className={`px-5 py-2 text-[10px] tracking-[0.18em] uppercase font-semibold rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap ${
                isScrolledOrMenuOpen
                  ? 'bg-[#222222] text-white hover:bg-[#001220]'
                  : 'bg-white text-[#222222] hover:bg-white/90'
              }`}
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              가맹 문의
            </Link>
          </nav>

          {/* Mobile Hamburger */}
          <button
            className="md:hidden flex flex-col gap-1.5 cursor-pointer p-2"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="메뉴 열기"
          >
            <span className={`block w-6 h-0.5 transition-all duration-300 ${isScrolledOrMenuOpen ? 'bg-[#222222]' : 'bg-white'}`} />
            <span className={`block w-6 h-0.5 transition-all duration-300 ${isScrolledOrMenuOpen ? 'bg-[#222222]' : 'bg-white'}`} />
            <span className={`block w-6 h-0.5 transition-all duration-300 ${isScrolledOrMenuOpen ? 'bg-[#222222]' : 'bg-white'}`} />
          </button>
        </div>

        {/* Mega Menu Panel — slide-in animation */}
        <div
          className="hidden md:flex absolute left-0 right-0 top-full bg-white/90 backdrop-blur-2xl border-t border-white/50 border-b border-b-black/8 overflow-hidden"
          style={{
            boxShadow: '0 8px 32px rgba(0,0,0,0.07)',
            maxHeight: megaOpen ? '400px' : '0px',
            opacity: megaOpen ? 1 : 0,
            transition: 'max-height 0.38s cubic-bezier(0.4,0,0.2,1), opacity 0.28s ease',
            pointerEvents: megaOpen ? 'auto' : 'none',
          }}
          onMouseEnter={() => {
            if (leaveTimerRef.current) clearTimeout(leaveTimerRef.current);
          }}
          onMouseLeave={handleNavMouseLeave}
        >
          {/* Left: Category List */}
          <div className="w-56 flex-shrink-0 border-r border-[#EFEFEF] py-8 flex flex-col gap-1">
            <p
              className="px-8 mb-3 text-[9px] tracking-[0.35em] uppercase text-[#BBBBBB] font-medium"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              MENU
            </p>
            {menuItemsWithSubs.map((item) => (
              <button
                key={item.label}
                onMouseEnter={() => handleCategoryChange(item.label)}
                onClick={() => handleCategoryChange(item.label)}
                className={`w-full text-left px-8 py-2.5 text-[11px] tracking-[0.2em] uppercase font-semibold transition-all duration-200 cursor-pointer flex items-center justify-between group ${
                  hoveredCategory === item.label
                    ? 'text-[#222222] bg-[#F7F7F7]'
                    : 'text-[#AAAAAA] hover:text-[#555555] hover:bg-[#FAFAFA]'
                }`}
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              >
                {item.label}
                {hoveredCategory === item.label && (
                  <i className="ri-arrow-right-s-line text-sm text-[#888]" />
                )}
              </button>
            ))}
          </div>

          {/* Right: Sub Items with fade transition */}
          <div className="flex-1 py-8 px-12 overflow-hidden">
            <div
              style={{
                opacity: contentVisible ? 1 : 0,
                transform: contentVisible ? 'translateY(0px)' : 'translateY(6px)',
                transition: 'opacity 0.18s ease, transform 0.18s ease',
              }}
            >
              {activeNavItem && activeNavItem.subs && (
                <>
                  <div className="flex items-center justify-between mb-6">
                    <h3
                      className="text-xs tracking-[0.3em] uppercase text-[#222222] font-semibold"
                      style={{ fontFamily: "'Montserrat', sans-serif" }}
                    >
                      {activeNavItem.label}
                    </h3>
                    <Link
                      to={activeNavItem.href}
                      className="text-[10px] tracking-[0.2em] uppercase text-[#888] hover:text-[#222222] transition-colors duration-200 cursor-pointer flex items-center gap-1.5"
                      style={{ fontFamily: "'Montserrat', sans-serif" }}
                    >
                      전체 보기
                      <i className="ri-arrow-right-line text-xs" />
                    </Link>
                  </div>

                  <div className="flex gap-10 flex-wrap">
                    {activeNavItem.subs.map((sub) => (
                      <div key={sub.href} className="min-w-[160px]">
                        <Link
                          to={sub.href}
                          className="block text-sm font-semibold text-[#222222] tracking-wide hover:text-[#888] transition-colors duration-200 cursor-pointer whitespace-nowrap mb-3"
                          style={{ fontFamily: "'Montserrat', 'Noto Sans KR', sans-serif" }}
                        >
                          {sub.label}
                        </Link>
                        {sub.details && sub.details.length > 0 && (
                          <ul className="flex flex-col gap-2 pl-1 border-l border-[#E8E8E8]">
                            {sub.details.map((detail) => (
                              <li key={detail.href} className="flex items-center gap-2">
                                <span className="text-[#AAAAAA] text-xs">•</span>
                                <Link
                                  to={detail.href}
                                  className="text-xs text-[#666] tracking-wide hover:text-[#222222] transition-colors duration-200 cursor-pointer whitespace-nowrap"
                                  style={{ fontFamily: "'Montserrat', 'Noto Sans KR', sans-serif" }}
                                >
                                  {detail.label}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    ))}
                  </div>

                  <div className="mt-8 pt-5 border-t border-black/8 flex items-center gap-8">
                    <span
                      className="text-[10px] tracking-[0.3em] uppercase text-[#BBBBBB] font-medium"
                      style={{ fontFamily: "'Montserrat', sans-serif" }}
                    >
                      SEONYUL LINE STUDIO — {activeNavItem.label}
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <div className="md:hidden bg-white/80 backdrop-blur-2xl border-t border-white/40 max-h-[80vh] overflow-y-auto">
            {navItems.map((item) => (
              <div key={item.label} className="border-b border-[#F5F5F5]">
                <button
                  className="w-full flex items-center justify-between px-8 py-4 cursor-pointer"
                  onClick={() =>
                    setMobileExpanded(mobileExpanded === item.label ? null : item.label)
                  }
                >
                  <span
                    className="text-[11px] tracking-[0.22em] uppercase font-semibold text-[#222222]"
                    style={{ fontFamily: "'Montserrat', sans-serif" }}
                  >
                    {item.label}
                  </span>
                  <i
                    className={`ri-arrow-down-s-line text-[#888] transition-transform duration-300 ${
                      mobileExpanded === item.label ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                {mobileExpanded === item.label && item.subs && (
                  <div className="px-8 pb-5 flex flex-col gap-4">
                    {item.subs.map((sub) => (
                      <div key={sub.href}>
                        <Link
                          to={sub.href}
                          className="block text-sm font-medium text-[#222222] mb-2 cursor-pointer"
                          style={{ fontFamily: "'Montserrat', 'Noto Sans KR', sans-serif" }}
                        >
                          {sub.label}
                        </Link>
                        {sub.details && (
                          <ul className="flex flex-col gap-1.5 pl-3 border-l border-[#E8E8E8]">
                            {sub.details.map((detail) => (
                              <li key={detail.href} className="flex items-center gap-2">
                                <span className="text-[#AAAAAA] text-xs">•</span>
                                <Link
                                  to={detail.href}
                                  className="text-xs text-[#666] cursor-pointer"
                                  style={{ fontFamily: "'Montserrat', 'Noto Sans KR', sans-serif" }}
                                >
                                  {detail.label}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <div className="px-8 py-5">
              <Link
                to="/community/inquiry"
                className="block w-full text-center px-6 py-3 bg-[#222222] text-white text-[10px] tracking-[0.18em] uppercase font-semibold rounded-full cursor-pointer whitespace-nowrap"
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              >
                가맹 문의
              </Link>
            </div>
          </div>
        )}
      </header>

      {/* Mega menu backdrop */}
      {megaOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/5 backdrop-blur-[2px]"
          style={{ top: '80px' }}
          onMouseEnter={handleNavMouseLeave}
        />
      )}
    </>
  );
}
